import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import { formatTime } from '../../../lib/gameUtils';
import { DifficultyDropdown } from './DifficultyDropdown';
import { GameActionButtons } from './GameActionButtons';
import { FeatureButtons } from './FeatureButtons';
import { NotificationBell } from '../NotificationBell';
import { 
  ChevronLeft, 
  Shuffle, 
  Pause, 
  Play, 
  RotateCcw,
  Trophy,
  Calendar,
  Swords,
  History,
  Settings,
  Lightbulb,
  Bell,
  HelpCircle
} from 'lucide-react';

export function DesktopHeader({
  time,
  moves,
  difficulty,
  onDifficultySelect,
  isPaused,
  isRunning,
  onPause,
  onPrevious,
  onNewImage,
  canGoBack,
  hasNewImages,
  onReset,
  onSettings,
  onLeaderboard,
  onDailyChallenge,
  onChallengeFriend,
  onHistory,
  onHint,
  onHelp,
  hasPlayedDailyToday,
  notificationCount,
  notifications,
  onMarkNotificationsRead,
  onViewChallengeNotification,
  hasUnplacedPieces,
  isComplete,
  imageUrl
}) {
  const [isImageOpen, setIsImageOpen] = useState(false);
  
  return (
    <div className="hidden lg:block w-full">
      {/* Row 1: Logo | Time | Difficulty | Moves | Reference Image */}
      <div className="flex items-center justify-between gap-6 mb-3">
        {/* Logo and Title */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="rounded-lg select-none overflow-hidden w-10 h-10">
            <img 
              src="/app-logo.png" 
              alt="Jigsaw Blocks" 
              className="w-full h-full object-contain"
            />
          </div>
          <span className="font-heading font-bold text-xl text-foreground">JigSaw Blocks</span>
        </div>
        
        {/* Time */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Time</span>
          <span className="text-2xl font-heading font-bold tabular-nums text-foreground">
            {formatTime(time)}
          </span>
        </div>
        
        {/* Difficulty Badge */}
        <DifficultyDropdown 
          difficulty={difficulty} 
          onDifficultySelect={onDifficultySelect}
          variant="desktop"
        />
        
        {/* Moves - positioned to NOT be covered by reference image */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Moves</span>
          <span className="text-2xl font-heading font-bold tabular-nums text-foreground">
            {moves}
          </span>
        </div>
        
        {/* Reference Image Thumbnail - Clickable to expand fullscreen */}
        <div className="flex-shrink-0 ml-4">
          <div 
            className="w-16 h-16 rounded-lg overflow-hidden border-2 border-border shadow-md cursor-pointer hover:border-primary/50 hover:scale-105 transition-all duration-200"
            onClick={() => setIsImageOpen(true)}
            data-testid="desktop-reference-thumbnail"
          >
            {imageUrl && (
              <img 
                src={imageUrl} 
                alt="Reference" 
                className="w-full h-full object-cover"
                draggable={false}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Fullscreen Reference Image Dialog */}
      <Dialog open={isImageOpen} onOpenChange={setIsImageOpen}>
        <DialogContent 
          className="max-w-4xl w-[95vw] p-0 bg-transparent border-none [&>button]:hidden cursor-pointer" 
          hideCloseButton
          onClick={() => setIsImageOpen(false)}
        >
          <DialogTitle className="sr-only">Reference Image</DialogTitle>
          <div onClick={() => setIsImageOpen(false)}>
            <img 
              src={imageUrl} 
              alt="Full reference" 
              className="w-full h-auto max-h-[85vh] object-contain rounded-xl shadow-2xl pointer-events-none"
              draggable={false}
            />
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Row 2: All Control Buttons in a single horizontal row */}
      <div className="flex items-center justify-center gap-1.5 flex-wrap">
        <TooltipProvider delayDuration={200}>
          {/* Previous Image */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onPrevious}
                disabled={!canGoBack}
                className={cn("h-10 w-10", !canGoBack && "opacity-50")}
                data-testid="desktop-prev-btn"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{canGoBack ? 'Previous Image' : 'No Previous'}</TooltipContent>
          </Tooltip>
          
          {/* Shuffle/New Image */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onNewImage}
                className={cn("h-10 w-10", !hasNewImages && "opacity-50")}
                data-testid="desktop-shuffle-btn"
              >
                <Shuffle className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{hasNewImages ? 'New Image' : 'No New Images'}</TooltipContent>
          </Tooltip>
          
          {/* Pause/Play */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onPause}
                disabled={!isRunning}
                className="h-10 w-10"
                data-testid="desktop-pause-btn"
              >
                {isPaused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isPaused ? 'Resume' : 'Pause'}</TooltipContent>
          </Tooltip>
          
          {/* Reset */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onReset}
                className="h-10 w-10"
                data-testid="desktop-reset-btn"
              >
                <RotateCcw className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reset Puzzle</TooltipContent>
          </Tooltip>
          
          {/* Hint */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onHint}
                disabled={!hasUnplacedPieces || isComplete || isPaused}
                className={cn("h-10 w-10", (!hasUnplacedPieces || isComplete || isPaused) && "opacity-50")}
                data-testid="desktop-hint-btn"
              >
                <Lightbulb className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Hint (-25 pts)</TooltipContent>
          </Tooltip>
          
          {/* Leaderboard */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onLeaderboard}
                className="h-10 w-10"
                data-testid="desktop-leaderboard-btn"
              >
                <Trophy className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Leaderboard</TooltipContent>
          </Tooltip>
          
          {/* Daily Challenge */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant={hasPlayedDailyToday ? "outline" : "default"}
                size="icon"
                onClick={onDailyChallenge}
                className={cn(
                  "h-10 w-10 relative",
                  !hasPlayedDailyToday && "bg-primary text-primary-foreground"
                )}
                data-testid="desktop-daily-btn"
              >
                <Calendar className="h-5 w-5" />
                {!hasPlayedDailyToday && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-destructive rounded-full" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Daily Challenge</TooltipContent>
          </Tooltip>
          
          {/* Challenge Friend */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onChallengeFriend}
                className="h-10 w-10"
                data-testid="desktop-challenge-btn"
              >
                <Swords className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>My Challenges</TooltipContent>
          </Tooltip>
          
          {/* History */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onHistory}
                className="h-10 w-10"
                data-testid="desktop-history-btn"
              >
                <History className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Puzzle History</TooltipContent>
          </Tooltip>
          
          {/* Notifications */}
          <NotificationBell
            notifications={notifications}
            count={notificationCount}
            onMarkAsRead={onMarkNotificationsRead}
            onViewChallenge={onViewChallengeNotification}
          />
          
          {/* Settings */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onSettings}
                className="h-10 w-10"
                data-testid="desktop-settings-btn"
              >
                <Settings className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Settings</TooltipContent>
          </Tooltip>
          
          {/* Help */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="outline" 
                size="icon"
                onClick={onHelp}
                className="h-10 w-10"
                data-testid="desktop-help-btn"
              >
                <HelpCircle className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Help</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
